import React, {
	useEffect,
	useState,
	useLayoutEffect,
	useImperativeHandle,
	useRef,
} from "react";

import {
	getCaseDisplayTags,
	getFirstCaseTopicFilterOption,
	normalizeCaseFilter,
} from "../../../data/cases/caseFilterHelpers";

import fortniteIcon from "../../../img/filter-icons/logo-fn.svg";
import unrealIcon from "../../../img/filter-icons/logo-ue.svg";
import choizIcon from "../../../img/filter-icons/logo-choiz.svg";

import SlantedTag from "../SlantedTag";

import styles from "./TagContainer.module.css";

/*
 * ============================================================
 * FILTER PRESENTATION
 * ============================================================
 *
 * Same identities currently used by Header.
 *
 * This is deliberately isolated in one object here so the
 * rendering code itself doesn't care which filters have icons.
 *
 * Next cleanup step can move this exact object into a shared
 * presentation helper used by Header + SolutionSlide +
 * TagContainer without changing this component's behaviour.
 */
const FILTER_PRESENTATION = {
	fortnite: {
		mark: "FN",
		image: fortniteIcon,
	},

	"choiz-io": {
		mark: "CH",
		image: choizIcon,
	},

	"unreal-engine": {
		mark: "UE",
		image: unrealIcon,
	},
};

const getFilterPresentation = (
	filter,
) => {
	const normalizedFilter =
		normalizeCaseFilter(
			filter,
		);

	return (
		FILTER_PRESENTATION[
			normalizedFilter
		] ?? null
	);
};

/*
 * ============================================================
 * FILTER IDENTITY
 * ============================================================
 *
 * Image first.
 *
 * Short mark only acts as fallback when the image cannot
 * be loaded.
 */
const FilterIdentity = ({
	filter,
}) => {
	const presentation =
		getFilterPresentation(
			filter?.value,
		);

	const [
		imageFailed,
		setImageFailed,
	] = useState(false);

	if (!presentation) {
		return null;
	}

	const hasImage =
		Boolean(
			presentation.image,
		) && !imageFailed;

	const hasMark =
		Boolean(
			presentation.mark,
		);

	if (
		!hasImage &&
		!hasMark
	) {
		return null;
	}

	return (
		<span
			className={`${styles.filterIcon}${
				hasImage
					? ` ${styles.filterIconHasImage}`
					: ""
			}`}
			aria-hidden="true"
		>
			{hasImage ? (
				<img
					className={
						styles.filterIconImage
					}
					src={
						presentation.image
					}
					alt=""
					draggable="false"
					onError={() => {
						setImageFailed(
							true,
						);
					}}
				/>
			) : (
				<span
					className={
						styles.filterIconFallback
					}
				>
					{
						presentation.mark
					}
				</span>
			)}
		</span>
	);
};

/*
 * ============================================================
 * TAG
 * ============================================================
 *
 * Identity tags:
 *
 * - filter
 * - work
 * - solution
 *
 * use the shared standard spacing.
 *
 *
 * Legacy content tags:
 *
 * - bg
 * - noBg
 * - more
 *
 * keep their existing legacy spacing.
 *
 *
 * That means we normalize the UI we actively maintain without
 * unexpectedly changing historical article-tag layout.
 */
const Tag = ({
	type,
	txt,
	filter = null,
}) => {
	const className =
		{
			noBg:
				"tag-span no-bg",

			more:
				"tag-span more no-bg more",

			moreAbsolute:
				"tag-span more no-bg more absolute",

			bg:
				"tag-span",

			work:
				`tag-span ${styles.typeTag} ${styles.workTag}`,

			solution:
				`tag-span ${styles.typeTag} ${styles.solutionTag}`,

			filter:
				`tag-span ${styles.filterTag}`,
		}[type] ??
		"tag-span";

	const isIdentityTag =
		type ===
			"filter" ||
		type ===
			"work" ||
		type ===
			"solution";

	const isTypeTag =
		type ===
			"work" ||
		type ===
			"solution";

	return (
		<SlantedTag
			as="span"
			className={
				className
			}
			standardSpacing={
				isIdentityTag
			}
			unslantIcons={
				isIdentityTag
			}
			style={{
				pointerEvents:
					"none",

				...(type ===
						"more" ||
				type ===
					"moreAbsolute"
					? {
							color:
								"#f10388",
					  }
					: {}),
			}}
		>
			{type ===
			"filter" ? (
				<FilterIdentity
					filter={
						filter
					}
				/>
			) : null}

			{isTypeTag ? (
				<span
					className={`${styles.typeIcon}${
						type ===
						"work"
							? ` ${styles.typeIconWork}`
							: ` ${styles.typeIconSolution}`
					}`}
					aria-hidden="true"
				/>
			) : null}

			<span
				className={
					type ===
					"filter"
						? styles.filterLabel
						: styles.tagLabel
				}
			>
				{txt}
			</span>

			{type ===
				"more" ||
			type ===
				"moreAbsolute" ? (
				<span className="moreBtnSelector">
					<span>
						↓
					</span>

					<span
						className="more"
						style={{
							opacity:
								1,
						}}
					>
						{" "}
						Learn more
					</span>
				</span>
			) : null}
		</SlantedTag>
	);
};

const TagContainer =
	React.forwardRef(
		(props, ref) => {
			const tagContainerRef =
				useRef(null);

			const [
				refresh,
				setRefresh,
			] = useState(
				Date.now,
			);

			/*
			 * ====================================================
			 * PRESENTATION DATA
			 * ====================================================
			 *
			 * Order:
			 *
			 * 1. First topical filter
			 * 2. Work / Solutions
			 * 3. Remaining legacy tags
			 *
			 * Any legacy tag which is also a filter has already
			 * been removed by getCaseDisplayTags().
			 */
			const firstTopicFilter =
				getFirstCaseTopicFilterOption(
					props.solution,
				);

			const displayTags =
				getCaseDisplayTags(
					props.solution,
				);

			const solutionType =
				normalizeCaseFilter(
					props.solution
						?.type,
				);

			/*
			 * Stable resize callback.
			 *
			 * The old version added and removed two different
			 * anonymous functions, meaning the original resize
			 * listener was never actually removed.
			 */
			useEffect(() => {
				const handleResize =
					() => {
						setRefresh(
							Date.now(),
						);
					};

				window.addEventListener(
					"resize",
					handleResize,
				);

				return () => {
					window.removeEventListener(
						"resize",
						handleResize,
					);
				};
			}, []);

			const refreshTagLayout =
				(external) => {
					const allChildren =
						Array.from(
							tagContainerRef
								.current
								?.children ??
								[],
						);

					if (
						allChildren.length >
						0
					) {
						let nextLine =
							0;

						let lastTop =
							-1;

						allChildren.forEach(
							(x) => {
								const rect =
									x.getBoundingClientRect();

								const top =
									rect.top;

								if (
									top >
									lastTop
								) {
									nextLine++;

									lastTop =
										top;
								}

								x.setAttribute(
									"data-line",
									nextLine,
								);

								x.setAttribute(
									"data-width",
									rect.width,
								);
							},
						);
					}

					if (!external) {
						props.refreshVisibility?.();
					}
				};

			useLayoutEffect(() => {
				refreshTagLayout(
					false,
				);
			}, [
				refresh,
				props.solution,
			]);

			const getRef =
				() => {
					return tagContainerRef;
				};

			useImperativeHandle(
				ref,
				() => {
					return {
						refreshTagLayout(
							external,
						) {
							refreshTagLayout(
								external,
							);
						},

						getRef() {
							return getRef();
						},
					};
				},
			);

			return (
				<div
					key={`tag-container-${
						props.solution
							?.name
					}`}
					className="section-tag-container article"
					ref={
						tagContainerRef
					}
					style={{
						justifyContent:
							"center",
					}}
				>
					{/*
					 * =============================================
					 * FIRST TOPIC FILTER
					 * =============================================
					 *
					 * Fortnite / Choiz.io / Unreal Engine / etc.
					 */}
					{firstTopicFilter ? (
						<Tag
							key={`tag-filter-${firstTopicFilter.value}-${props.solution?.name}`}
							type="filter"
							filter={
								firstTopicFilter
							}
							txt={
								firstTopicFilter.label
							}
						/>
					) : null}

					{/*
					 * =============================================
					 * WORK / SOLUTIONS
					 * =============================================
					 *
					 * Kept as its own pill even when a topical
					 * filter exists.
					 */}
					<Tag
						key={`tag-type-${props.solution?.name}`}
						type={
							solutionType ===
							"solution"
								? "solution"
								: "work"
						}
						txt={
							solutionType ===
							"solution"
								? "solution"
								: "work"
						}
					/>

					{/*
					 * =============================================
					 * LEGACY TAGS
					 * =============================================
					 *
					 * Filter duplicates and Work / Solutions have
					 * already been removed.
					 */}
					{displayTags.map(
						(
							tag,
							i,
						) => {
							const isNoBg =
								tag
									.trimStart()
									.startsWith(
										"#",
									);

							return (
								<Tag
									key={`tag-${i}-${tag}`}
									type={
										isNoBg
											? "noBg"
											: "bg"
									}
									txt={
										tag
									}
								/>
							);
						},
					)}
				</div>
			);
		},
	);

export default TagContainer;